import {Injectable} from '@angular/core';
import {UnsubscribeOnDestroyAdapter} from "@shared";
import {BehaviorSubject} from "rxjs";
import {Prescription} from "@core/models/Prescription";
import {FilterReceta} from "@core/models/FilterReceta";
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from "@angular/common/http";
import {DefaultResponse} from "@core/models/Http/DefaultResponse";
import {environment} from "@enviroment/environment";
import {PrescriptionData} from "@core/models/PrescriptionData";
import {RecetaCompleta} from "@core/interfaces/receta-completa";
import {UserRole} from "@core/models/Enums/UserRole";
import {FilterRecetaPatient} from "@core/models/FilterRecetaPatient";
import {IDetalleReceta} from "@core/interfaces/receta-data";
import {IReaccion} from "@core/models/Reaccion";
import {IMedicamentoActivo} from "@core/interfaces/medicamento-activo";
import {FilterRecetaDoctor} from "@core/models/FilterRecetaDoctor";
import {HttpMesageHandlerService} from "@core/service/http-mesage-handler";
import {MsgResponse} from "@core/models/MsgResponse";
import {RepositorioPacienteResponse} from "@core/interfaces/repositorio-paciente";

@Injectable({
  providedIn: 'root'
})
export class PrescriptionService extends UnsubscribeOnDestroyAdapter {
  isTblLoading = true;
  dataChange: BehaviorSubject<Prescription[]> = new BehaviorSubject<Prescription[]>([]);

  constructor(
    private httpClient: HttpClient,
    private httpMsgHandler: HttpMesageHandlerService
  ) {
    super();
  }

  get data(): Prescription[] {
    return this.dataChange.value;
  }

  /** CRUD METHODS */
  getAllprescriptions(filterReceta: FilterReceta, nameFilter: string, idFilter: string, control: boolean): void {
    if (control) {
      this.isTblLoading = false;
      this.dataChange.next([]);
      return;
    }
    this.isTblLoading = true;
    const endpoint = nameFilter === UserRole.Medico
      ? `${environment.backend_url}Recetas/GetFilteredRecetasByIdMedico`
      : `${environment.backend_url}Recetas/GetFilteredRecetas`;

    this.subs.sink = this.httpClient.post<DefaultResponse<Prescription[]>>(
      endpoint, filterReceta
    ).subscribe({
      next: (response: DefaultResponse<Prescription[]>): void => {
        this.isTblLoading = false;
        this.processPrescriptions(response.data, nameFilter, idFilter);
      },
      error: (error: HttpErrorResponse): void => this.handleMsg(error)
    });
  }

  getPrescriptionDetails(idReceta: string, pin: string | null) {
    const params: any = {
      idReceta
    };

    if (pin) {
      params.pin = pin;
    }

    return this.httpClient.get<DefaultResponse<PrescriptionData>>(
      `${environment.backend_url}Recetas/ConsultarReceta`,
      { params }
    );
  }

  getPrescriptionDetailsForPatient(idReceta: string, idPaciente: string | null, pin: string | null) {
    return this.httpClient.post<DefaultResponse<IDetalleReceta[]>>(
      `${environment.backend_url}DetalleRecetas/GetDetallesByIdReceta`,
      {
        idReceta,
        idPaciente: idPaciente ?? null,
        pin: pin ?? null
      },
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  reaccionCrearActualizar(reaccion: IReaccion) {
    return this.httpClient.post<DefaultResponse<IDetalleReceta[]>>(
      `${environment.backend_url}DetalleRecetas/reaccion-crear-actualizar`,
      reaccion
    );
  }

  getPrescriptionById(receta: string, pin: string | null = null) {
    let params = new HttpParams().set('receta', receta);

    if (pin) {
      params = params.set('pin', pin);
    }

    return this.httpClient.get<DefaultResponse<RecetaCompleta>>(
      `${environment.backend_url}PuntoVenta/consultar-receta`,
      { params }
    );
  }

  getPrescriptionByQr(prescriptionQR: string, pin: string | null = null) {
    let params = new HttpParams();
    if (pin) params = params.set('pin', pin);

    return this.httpClient.post<DefaultResponse<RecetaCompleta>>(
      `${environment.backend_url}PuntoVenta/buscar-receta`,
      JSON.stringify(prescriptionQR),
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        params
      }
    );
  }

  deletePrescription(id: string): void {
    this.httpClient.delete(`${environment.backend_url}Recetas/EliminarReceta/${id}`)
      .subscribe({
        next: (res) => {
          this.httpMsgHandler.handleMsg(res as MsgResponse);
        },
        error: (error: HttpErrorResponse) => {
          this.httpMsgHandler.handleMsg(error);
        },
      });
  }

  getPrescriptionByIdPatient(
    filterReceta: FilterRecetaPatient,
    nameFilter: string = '',
    idFilter: string = ''
  ): void {
    this.isTblLoading = true;

    this.subs.sink = this.httpClient.post<DefaultResponse<Prescription[]>>(
      `${environment.backend_url}Recetas/GetRecetasByIdPaciente`,
      filterReceta
    ).subscribe({
      next: (response: DefaultResponse<Prescription[]>): void => {
        // ✅ NO apagues aquí; lo apaga processPrescriptions en finally
        this.processPrescriptions(response?.data, nameFilter, idFilter);
      },
      error: (error: HttpErrorResponse): void => {
        // ✅ en error, apagar loader y limpiar tabla
        this.isTblLoading = false;
        this.dataChange.next([]);
        this.handleMsg(error);
      }
    });
  }

  getPrescriptionByIdDoctor(filterReceta: FilterRecetaDoctor): void {
    if (!filterReceta.idMedico) {
      this.isTblLoading = false;
      this.dataChange.next([]);
      return;
    }

    this.isTblLoading = true;
    this.subs.sink = this.httpClient.get<DefaultResponse<Prescription[]>>(
      `${environment.backend_url}Recetas/RecetasByIdMedico?idSucursal=${filterReceta.idSucursal}&idMedico=${filterReceta.idMedico}`,
    ).subscribe({
      next: (response: DefaultResponse<Prescription[]>): void => {
        this.isTblLoading = false;
        this.processPrescriptions(response.data, '', '');
      },
      error: (error: HttpErrorResponse): void => this.handleMsg(error)
    });
  }

  getQr(idPrescription: string) {
    return this.httpClient.post<DefaultResponse<string>>(
      `${environment.backend_url}Recetas/GetQRByIdReceta`,
      JSON.stringify(idPrescription),
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      }
    );
  }

  enviarRecetaPorCorreo(payload: { idReceta: string; pdfBase64: string }) {
    return this.httpClient.post<DefaultResponse<boolean>>(
      `${environment.backend_url}Recetas/EnviarRecetaPorCorreo`,
      payload,
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      }
    );
  }

  getActiveMedications(idPaciente: string | null, pin: string | null) {
    const params = new HttpParams()
      .set('idPaciente', idPaciente ?? '')
      .set('pin', pin ?? '');

    return this.httpClient.get<DefaultResponse<IMedicamentoActivo[]>>(
      `${environment.backend_url}DetalleRecetas/GetSoloMedicamentosActivos`,
      { params }
    );
  }

  getRepositorioPaciente(idPaciente?: string | null, pin?: string | null) {
    let params = new HttpParams();

    if (idPaciente) {
      params = params.set('idPaciente', idPaciente);
    }
    if (pin) {
      params = params.set('pin', pin);
    }

    return this.httpClient.get<DefaultResponse<RepositorioPacienteResponse>>(
      `${environment.backend_url}MedicamentoActivo/repositorio-paciente`,
      { params }
    );
  }

  getRepositorioPacienteHtml(idPaciente?: string | null, pin?: string | null) {
    let params = new HttpParams();

    if (idPaciente) {
      params = params.set('idPaciente', idPaciente);
    }
    if (pin) {
      params = params.set('pin', pin);
    }

    return this.httpClient.get(
      `${environment.backend_url}MedicamentoActivo/repositorio-paciente-html`,
      {
        headers: new HttpHeaders({ Accept: 'text/html' }),
        params,
        responseType: 'text'
      }
    );
  }

  private processPrescriptions(
    data: Prescription[] | null | undefined,
    nameFilter: string,
    idFilter: string
  ): void {
    try {
      const list: Prescription[] = Array.isArray(data) ? data : [];

      if (list.length === 0) {
        this.dataChange.next([]);
        return;
      }

      let filteredData = list.map((prescription) => {
        const fecha = (prescription?.fechaCreacion ?? '').toString();
        const fechaSolo = fecha.includes('T') ? fecha.split('T')[0] : fecha;

        return {
          ...prescription,
          fechaCreacion: fechaSolo,
          edad: this.calculateAge(prescription?.fechaNacimientoPaciente),
        };
      });

      if (nameFilter === UserRole.Medico) {
        filteredData = filteredData.filter((p) => p.idMedico === idFilter);
      }

      this.dataChange.next(filteredData);

    } catch (e) {
      this.dataChange.next([]);
    } finally {
      this.isTblLoading = false;
    }
  }

  private handleMsg(error: HttpErrorResponse): void {
    this.isTblLoading = false;
    this.httpMsgHandler.handleMsg(error);
  }

  private calculateAge(fechaNacimiento: string): number {
    if (!fechaNacimiento) {
      return 0;
    }
    const birthDate = new Date(fechaNacimiento.split('T')[0]);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }
}
